import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  ArrowRight,
  Store,
  Truck,
  ShieldCheck,
  Tag,
  MapPin,
  CheckCircle,
  Home,
  Briefcase,
  Bookmark,
  ShoppingCart
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import { geocodeAddress } from "../services/mapService";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CartPage() {
  const navigate = useNavigate();
  const { items, itemsByStore, removeItem, updateQuantity, subtotal, taxes, itemCount, clearCart, shippingAddress, setShippingAddress, serviceFees, selectedShipping, savedForLater, saveForLater, moveToCart, removeSaved } = useCart();
  const { isAuthenticated, user, refreshUser } = useAuth();

  // We no longer calculate delivery here. It's done on OrderPreviewPage.
  // We no longer manage addresses here either.
  const localTotal = subtotal + (taxes || 0) + (serviceFees || 0);

  // Recommendations
  const [recommendations, setRecommendations] = useState([]);

  const fetchRecommendations = React.useCallback(async () => {
    try {
      if (items.length === 0) {
        const res = await axios.get(`${API_URL}/products?limit=8`);
        setRecommendations(res.data.slice(0, 8));
        return;
      }

      const categoryIds = [...new Set(items.map(i => i.product?.category_id).filter(Boolean))];
      const cartIds = items.map(i => i.product?.id);

      if (categoryIds.length > 0) {
        // Fetch by category of first cart item
        const res = await axios.get(`${API_URL}/products?category_id=${categoryIds[0]}&limit=12`);
        const filtered = res.data.filter(p => !cartIds.includes(p.id)).slice(0, 8);
        if (filtered.length > 0) {
          setRecommendations(filtered);
          return;
        }
      }

      // Fallback: latest products
      const res = await axios.get(`${API_URL}/products?limit=12`);
      setRecommendations(res.data.filter(p => !cartIds.includes(p.id)).slice(0, 8));
    } catch (e) {
      console.error("Recommendations error:", e);
    }
  }, [items]);


  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const handleProceedToPreview = () => {
    if (!isAuthenticated) {
      toast.error("Veuillez vous connecter pour continuer");
      return;
    }
    navigate("/commande/apercu");
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-12 w-12 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Votre panier est vide</h1>
            <p className="text-gray-600 mb-6">
              Découvrez nos produits et commencez vos achats !
            </p>
            <Link to="/catalogue">
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="browse-products">
                Parcourir les produits
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Saved items even when cart is empty */}
          {savedForLater.length > 0 && (
            <div className="mt-12 max-w-2xl mx-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Bookmark className="h-5 w-5 text-gray-500" />
                Enregistrés pour plus tard ({savedForLater.length})
              </h2>
              <div className="space-y-3">
                {savedForLater.map((item) => (
                  <Card key={item.product.id} className="bg-white shadow-sm">
                    <CardContent className="p-4 flex gap-4 items-center">
                      <Link to={`/produit/${item.product.id}`}>
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img src={item.product.images?.[0] || ""} alt={item.product.title} className="w-full h-full object-cover" />
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm line-clamp-1">{item.product.title}</p>
                        <p className="text-sm font-semibold text-orange-600 mt-0.5">
                          {((item.product.discount_price || item.product.price) * item.quantity).toFixed(2)} HTG
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
                          onClick={() => { moveToCart(item.product.id); toast.success("Article remis au panier"); }}>
                          <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Remettre au panier
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 text-xs"
                          onClick={() => { removeSaved(item.product.id); toast.success("Article supprimé"); }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  const storeEntries = Object.entries(itemsByStore);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Votre panier
        </h1>
        <p className="text-gray-600 mb-8">
          {itemCount} article{itemCount > 1 ? "s" : ""} dans votre panier
          {savedForLater.length > 0 && (
            <span className="ml-2 text-gray-400">· {savedForLater.length} enregistré{savedForLater.length > 1 ? "s" : ""} pour plus tard</span>
          )}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items grouped by Store */}
          <div className="lg:col-span-2 space-y-6">
            {storeEntries.map(([storeId, storeData]) => (
              <Card key={storeId} className="overflow-hidden bg-white shadow-md">
                {/* Store Header */}
                <div className="bg-gray-50 border-b px-6 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Store className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{storeData.storeName}</h3>
                    <p className="text-sm text-gray-500">
                      {storeData.items.length} article{storeData.items.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Badge className="bg-blue-50 text-blue-700 font-normal">
                      <Truck className="h-3 w-3 mr-1" />
                      Frais calculés à l'étape suivante
                    </Badge>
                  </div>
                </div>

                {/* Products */}
                <CardContent className="p-0 divide-y">
                  {storeData.items.map((item) => (
                    <div key={item.product.id} className="p-6 flex gap-4">
                      <Link to={`/produit/${item.product.id}`}>
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={
                              item.product.images?.[0] ||
                              "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400"
                            }
                            alt={item.product.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform"
                          />
                        </div>
                      </Link>

                      <div className="flex-1 min-w-0">
                        <div className="mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500/80">
                            {item.product.store?.name || "Boutique"}
                          </span>
                        </div>
                        <Link to={`/produit/${item.product.id}`} className="block mb-1">
                          <h3
                            className="font-medium text-gray-900 line-clamp-2 hover:text-orange-600 transition-colors text-sm leading-tight h-10"
                            title={item.product.title}
                          >
                            {item.product.title}
                          </h3>
                        </Link>

                        <div className="mt-2">
                          {item.product.discount_price ? (
                            <div className="flex flex-col">
                              <span className="text-lg font-bold text-red-600">{(item.product.discount_price || 0).toFixed(2)} HTG</span>
                              <span className="text-sm text-gray-400 line-through">{(item.product.price || 0).toFixed(2)} HTG</span>
                            </div>
                          ) : (
                            <p className="text-lg font-bold text-orange-600">
                              {(item.product.price || 0).toFixed(2)} HTG
                            </p>
                          )}
                        </div>

                        {/* Shipping Info for Item */}
                        <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                          <Truck className="h-3 w-3" />
                          {item.product.estimated_shipping_cost != null ? (
                            item.product.estimated_shipping_cost === 0 ? "Livraison Gratuite" : `+ ${Number(item.product.estimated_shipping_cost).toFixed(0)} HTG Livraison`
                          ) : (
                            // Fallback using store threshold if available, otherwise just say calculated
                            (item.product.store?.delivery_criteria?.free_delivery_threshold > 0 &&
                              item.product.price >= item.product.store.delivery_criteria.free_delivery_threshold) ? "Livraison Gratuite" : "+ Livraison calculée"
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              disabled={
                                item.quantity <= ((item.product.is_resell || item.product.store?.store_type?.toUpperCase() !== 'SUPPLIER')
                                  ? 1
                                  : (item.product.min_wholesale_quantity || 1))
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-10 text-center font-medium">
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              disabled={item.quantity >= item.product.stock_quantity}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>

                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 text-xs px-2"
                              onClick={() => {
                                saveForLater(item.product.id);
                                toast.success("Article enregistré pour plus tard");
                              }}
                            >
                              <Bookmark className="h-3.5 w-3.5 mr-1" />
                              Garder
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2"
                              onClick={() => {
                                removeItem(item.product.id);
                                toast.success("Article supprimé");
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                if (!window.confirm("Vider tout le panier ? Cette action supprime tous les articles.")) return;
                clearCart();
                toast.success("Panier vidé");
              }}
            >
              Vider le panier
            </Button>
          </div>

          {/* Order Summary & Address */}
          <div className="lg:col-span-1 space-y-6">


            <Card className="sticky top-32 bg-white shadow-lg">
              <CardContent className="p-6 space-y-4">
                <h2 className="font-bold text-xl">Résumé</h2>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sous-total</span>
                    <span>{(subtotal || 0).toFixed(2)} HTG</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Taxes</span>
                    <span className="font-medium text-gray-900">{(taxes || 0).toFixed(2)} HTG</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Livraison</span>
                    <span className="text-gray-500 text-xs text-right max-w-[140px]">Calculée à l'étape suivante</span>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between font-bold text-lg">
                  <span>Total estimé</span>
                  <span className="text-orange-600">{(localTotal || 0).toFixed(2)} HTG</span>
                </div>

                <Button
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-lg font-semibold"
                  onClick={handleProceedToPreview}
                >
                  Voir le récapitulatif
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Enregistrés pour plus tard */}
        {savedForLater.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-gray-500" />
              Enregistrés pour plus tard
              <span className="text-sm font-normal text-gray-500">({savedForLater.length})</span>
            </h2>
            <div className="space-y-3">
              {savedForLater.map((item) => (
                <Card key={item.product.id} className="bg-white shadow-sm border border-gray-200">
                  <CardContent className="p-4 flex gap-4 items-center">
                    <Link to={`/produit/${item.product.id}`}>
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={item.product.images?.[0] || ""}
                          alt={item.product.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500/80">
                        {item.product.store?.name || "Boutique"}
                      </p>
                      <Link to={`/produit/${item.product.id}`}>
                        <p className="font-medium text-gray-900 text-sm line-clamp-1 hover:text-orange-600">
                          {item.product.title}
                        </p>
                      </Link>
                      <p className="text-sm font-semibold text-orange-600 mt-0.5">
                        {((item.product.discount_price || item.product.price) * item.quantity).toFixed(2)} HTG
                        {item.quantity > 1 && (
                          <span className="text-xs text-gray-400 font-normal ml-1">× {item.quantity}</span>
                        )}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 items-end shrink-0">
                      <Button
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8 px-3"
                        onClick={() => {
                          moveToCart(item.product.id);
                          toast.success("Article remis au panier");
                        }}
                      >
                        <ShoppingCart className="h-3.5 w-3.5 mr-1" />
                        Remettre au panier
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs h-7 px-2"
                        onClick={() => {
                          removeSaved(item.product.id);
                          toast.success("Article supprimé");
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Vous aimerez aussi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recommendations.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
